import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@excalidraw/excalidraw/i18n";
import {
  LoadIcon,
  PlusIcon,
  CloseIcon,
  TrashIcon,
  collapseDownIcon,
  collapseUpIcon,
  pencilIcon,
  DuplicateIcon,
} from "@excalidraw/excalidraw/components/icons";
import { InlineIcon } from "@excalidraw/excalidraw/components/InlineIcon";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import {
  listProjects,
  createProject,
  updateProject,
  deleteProject,
  listWorkspaces,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  duplicateWorkspace,
  getCurrentProjectId,
  getCurrentWorkspaceId,
  hasCurrentWorkspace,
} from "../../data/projectsStorage";

import "./ProjectsHome.scss";

import type { Project, Workspace } from "../../data/projectsStorage/types";
import type { ProjectId, WorkspaceId } from "../../data/projectsStorage/types";

export const ProjectsHome: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onOpenWorkspace: (
    projectId: ProjectId,
    workspaceId: WorkspaceId,
  ) => Promise<void>;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}> = React.memo(({ isOpen, onClose, onOpenWorkspace, excalidrawAPI }) => {
  const { t } = useI18n();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(
    () => !hasCurrentWorkspace(),
  );

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  // Which project is showing the "new workspace" inline input
  const [creatingInProjectId, setCreatingInProjectId] =
    useState<ProjectId | null>(null);
  const [newWsName, setNewWsName] = useState("");
  const newWsInputRef = useRef<HTMLInputElement>(null);

  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<ProjectId>>(
    new Set(),
  );
  const [projectRootWorkspaces, setProjectRootWorkspaces] = useState<
    Record<ProjectId, Workspace[]>
  >({});

  const refresh = useCallback(async () => {
    setLoading(true);
    const projs = await listProjects();
    setProjects(projs);
    setLoading(false);
    return projs;
  }, []);

  useEffect(() => {
    if (isOpen) {
      setEditingId(null);
      setCreatingInProjectId(null);
      setNewWsName("");
      setExpandedProjectIds(new Set());
      setProjectRootWorkspaces({});

      refresh().then(async (projs) => {
        if (projs.length === 0) {
          const p = await createProject("Default");
          const ws = await createWorkspace(p.id, null, "Default");
          await onOpenWorkspace(p.id, ws.id);
          setBootstrapping(false);
          onClose();
        } else {
          setBootstrapping(false);
        }
      });
    }
  }, [isOpen, refresh, onOpenWorkspace, onClose]);

  const toggleProject = useCallback((id: ProjectId) => {
    setExpandedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const loadProjectData = useCallback(async (projectId: ProjectId) => {
    const workspaces = await listWorkspaces(projectId, null);
    setProjectRootWorkspaces((p) => ({ ...p, [projectId]: workspaces }));
  }, []);

  useEffect(() => {
    expandedProjectIds.forEach((id) => {
      if (!projectRootWorkspaces[id]) {
        loadProjectData(id);
      }
    });
  }, [expandedProjectIds, loadProjectData, projectRootWorkspaces]);

  // Focus the new-workspace input when it appears
  useEffect(() => {
    if (creatingInProjectId && newWsInputRef.current) {
      newWsInputRef.current.focus();
    }
  }, [creatingInProjectId]);

  // ---- Actions ----

  const handleCreateProject = async () => {
    const p = await createProject(t("projectsHome.untitledProject"));
    await createWorkspace(p.id, null, "Default");
    await refresh();
    // Auto-expand the new project
    setExpandedProjectIds((prev) => new Set(prev).add(p.id));
  };

  const startCreatingWorkspace = (projectId: ProjectId) => {
    setCreatingInProjectId(projectId);
    setNewWsName("");
  };

  const commitCreateWorkspace = async (projectId: ProjectId) => {
    const name = newWsName.trim();
    if (!name) {
      setCreatingInProjectId(null);
      setNewWsName("");
      return;
    }
    await createWorkspace(projectId, null, name);
    setCreatingInProjectId(null);
    setNewWsName("");
    const ws = await listWorkspaces(projectId, null);
    setProjectRootWorkspaces((p) => ({ ...p, [projectId]: ws }));
  };

  const cancelCreateWorkspace = () => {
    setCreatingInProjectId(null);
    setNewWsName("");
  };

  const handleOpenWorkspace = async (
    projectId: ProjectId,
    workspaceId: WorkspaceId,
  ) => {
    if (!excalidrawAPI) {
      return;
    }
    await onOpenWorkspace(projectId, workspaceId);
    onClose();
  };

  const startRename = (id: string, name: string) => {
    setEditingId(id);
    setEditName(name);
  };

  const handleRename = async (
    kind: "project" | "workspace",
    id: string,
    name: string,
    projectId?: ProjectId,
  ) => {
    const trimmed = name.trim();
    if (!trimmed) {
      setEditingId(null);
      setEditName("");
      return;
    }
    if (kind === "project") {
      await updateProject(id as ProjectId, { name: trimmed });
      setEditingId(null);
      setEditName("");
      refresh();
      return;
    }
    if (kind === "workspace" && projectId != null) {
      await updateWorkspace(id as WorkspaceId, { name: trimmed });
      setEditingId(null);
      setEditName("");
      const ws = await listWorkspaces(projectId, null);
      setProjectRootWorkspaces((p) => ({ ...p, [projectId]: ws }));
    }
  };

  const handleDuplicateWorkspace = async (
    projectId: ProjectId,
    workspaceId: WorkspaceId,
  ) => {
    const created = await duplicateWorkspace(workspaceId);
    if (created) {
      const ws = await listWorkspaces(projectId, null);
      setProjectRootWorkspaces((p) => ({ ...p, [projectId]: ws }));
    }
  };

  const handleDeleteProject = async (id: ProjectId) => {
    if (!window.confirm(t("projectsHome.confirmDeleteProject"))) {
      return;
    }
    await deleteProject(id);
    setProjectRootWorkspaces((p) => {
      const u = { ...p };
      delete u[id];
      return u;
    });
    refresh();
  };

  const handleDeleteWorkspace = async (
    id: WorkspaceId,
    projectId: ProjectId,
  ) => {
    if (!window.confirm(t("projectsHome.confirmDeleteWorkspace"))) {
      return;
    }
    await deleteWorkspace(id);
    const ws = await listWorkspaces(projectId, null);
    setProjectRootWorkspaces((p) => ({ ...p, [projectId]: ws }));
  };

  if (!isOpen || bootstrapping) {
    return null;
  }

  // ---- Render ----

  return (
    <div className="projects-home-overlay" onClick={onClose}>
      <div
        className="projects-home excalidraw"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="projects-home__header">
          <h2 className="projects-home__title">{t("projectsHome.title")}</h2>
          <div className="projects-home__header-actions">
            <button
              type="button"
              className="projects-home__btn-header"
              onClick={handleCreateProject}
              title={t("projectsHome.newProject")}
              aria-label={t("projectsHome.newProject")}
            >
              <InlineIcon icon={PlusIcon} size="1.25rem" />
            </button>
            <button
              type="button"
              className="projects-home__btn-header projects-home__btn-header--close"
              onClick={onClose}
              aria-label={t("projectsHome.close")}
            >
              <InlineIcon icon={CloseIcon} size="1.25rem" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="projects-home__body">
          {loading ? (
            <p className="projects-home__empty">{t("projectsHome.loading")}</p>
          ) : projects.length === 0 ? (
            <div className="projects-home__empty">
              <p>{t("projectsHome.howItWorks")}</p>
            </div>
          ) : (
            <ul className="projects-home__list">
              {projects.map((proj) => {
                const expanded = expandedProjectIds.has(proj.id);
                const workspaces = projectRootWorkspaces[proj.id] ?? [];
                const isCreating = creatingInProjectId === proj.id;
                const isCurrent = getCurrentProjectId() === proj.id;

                return (
                  <li key={proj.id}>
                    {/* ---- Project row ---- */}
                    <div
                      className="projects-home__project-row"
                      onClick={() => toggleProject(proj.id)}
                    >
                      <span className="projects-home__row-icon">
                        <InlineIcon
                          icon={expanded ? collapseUpIcon : collapseDownIcon}
                          size="1rem"
                        />
                      </span>
                      <span className="projects-home__row-icon">
                        <InlineIcon icon={LoadIcon} size="1rem" />
                      </span>

                      {editingId === proj.id ? (
                        <input
                          className="projects-home__inline-input"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onBlur={() =>
                            handleRename("project", proj.id, editName)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleRename("project", proj.id, editName);
                            }
                            if (e.key === "Escape") {
                              setEditingId(null);
                              setEditName("");
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      ) : (
                        <span className="projects-home__project-name">
                          {proj.name}
                        </span>
                      )}

                      <span className="projects-home__row-actions">
                        <button
                          type="button"
                          className="projects-home__btn-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            startRename(proj.id, proj.name);
                          }}
                          title={t("projectsHome.rename")}
                          aria-label={t("projectsHome.rename")}
                        >
                          <InlineIcon icon={pencilIcon} size="1rem" />
                        </button>
                        <button
                          type="button"
                          className="projects-home__btn-icon projects-home__btn-icon--danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(proj.id);
                          }}
                          title={t("projectsHome.deleteProject")}
                          aria-label={t("projectsHome.deleteProject")}
                        >
                          <InlineIcon icon={TrashIcon} size="1rem" />
                        </button>
                      </span>
                    </div>

                    {/* ---- Expanded content ---- */}
                    {expanded && (
                      <div className="projects-home__project-content">
                        {/* Workspace list */}
                        {workspaces.map((w) => (
                          <div
                            key={w.id}
                            className="projects-home__ws-row"
                            onClick={() => handleOpenWorkspace(proj.id, w.id)}
                          >
                            {editingId === w.id ? (
                              <input
                                className="projects-home__inline-input"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onBlur={() =>
                                  handleRename(
                                    "workspace",
                                    w.id,
                                    editName,
                                    proj.id,
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    handleRename(
                                      "workspace",
                                      w.id,
                                      editName,
                                      proj.id,
                                    );
                                  }
                                  if (e.key === "Escape") {
                                    setEditingId(null);
                                    setEditName("");
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                            ) : (
                              <span className="projects-home__ws-name">
                                {w.name}
                              </span>
                            )}

                            {isCurrent && getCurrentWorkspaceId() === w.id && (
                              <span className="projects-home__current-dot">
                                ●
                              </span>
                            )}

                            <span className="projects-home__row-actions">
                              <button
                                type="button"
                                className="projects-home__btn-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startRename(w.id, w.name);
                                }}
                                title={t("projectsHome.rename")}
                                aria-label={t("projectsHome.rename")}
                              >
                                <InlineIcon icon={pencilIcon} size="1rem" />
                              </button>
                              <button
                                type="button"
                                className="projects-home__btn-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDuplicateWorkspace(proj.id, w.id);
                                }}
                                title={t("projectsHome.duplicateWorkspace")}
                                aria-label={t(
                                  "projectsHome.duplicateWorkspace",
                                )}
                              >
                                <InlineIcon icon={DuplicateIcon} size="1rem" />
                              </button>
                              <button
                                type="button"
                                className="projects-home__btn-icon projects-home__btn-icon--danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteWorkspace(w.id, proj.id);
                                }}
                                title={t("projectsHome.deleteWorkspace")}
                                aria-label={t("projectsHome.deleteWorkspace")}
                              >
                                <InlineIcon icon={TrashIcon} size="1rem" />
                              </button>
                            </span>
                          </div>
                        ))}

                        {/* Inline "new workspace" input (shown after clicking + Workspace) */}
                        {isCreating && (
                          <div className="projects-home__ws-row projects-home__ws-row--new">
                            <input
                              ref={newWsInputRef}
                              className="projects-home__inline-input"
                              placeholder={t(
                                "projectsHome.newWorkspaceNamePlaceholder",
                              )}
                              value={newWsName}
                              onChange={(e) => setNewWsName(e.target.value)}
                              onBlur={() => commitCreateWorkspace(proj.id)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  commitCreateWorkspace(proj.id);
                                }
                                if (e.key === "Escape") {
                                  cancelCreateWorkspace();
                                }
                              }}
                            />
                          </div>
                        )}

                        {/* "+ Workspace" button */}
                        {!isCreating && (
                          <button
                            type="button"
                            className="projects-home__add-ws-btn"
                            onClick={() => startCreatingWorkspace(proj.id)}
                          >
                            <InlineIcon icon={PlusIcon} size="0.875rem" />
                            <span>{t("projectsHome.newWorkspace")}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
});

ProjectsHome.displayName = "ProjectsHome";
